(function() {
    'use strict';
  
    /********************************************************************
     * CONTEXT
     * 
     * Kizen's Shopify theme uses Shopify's built-in cookie banner
     * functionality. However, Shopify's implementation has some issues:
     * it stores the user's cookie consent but that information
     * does not modify the shouldShowCookieBanner() function. This means that
     * even after the user has accepted or declined cookies, the banner
     * may still appear on future visits. 
     ********************************************************************/
    
    /********************************************************************
     * CONFIGURATION
     ********************************************************************/
    
    // Name of the cookie we set ourselves to remember the user's choice
    const CONSENT_COOKIE_NAME = 'shopify_user_consented';
  
    // How long we should remember the decision (in days)
    const COOKIE_EXPIRY_DAYS = 365;
  
    // ID of Shopify's cookie banner container
    const BANNER_ID = 'shopify-pc__banner';
  
    // Toggle this to true when debugging — logs will appear in console
    const DEBUG = false;
  
  
    /********************************************************************
     * HELPER LOGGING
     ********************************************************************/
    function log(...args) {
      if (DEBUG) console.log('[ConsentFix]', ...args);
    }
  
  
    /********************************************************************
     * COOKIE HELPERS
     ********************************************************************/
  
    // Reads a cookie by name
    function getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        const cookieVal = parts.pop().split(';').shift();
        // log('Read cookie:', name, '→', cookieVal);
        return cookieVal;
      }
      return null;
    }
  
    // Writes a cookie (Lax & path=/ for minimal issues)
    function setCookie(name, value, days) {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
      document.cookie =
        `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  
      // log('Set cookie:', name, '=', value);
    }
  
  
    /********************************************************************
     * CONSENT STATE CHECKER
     *
     * This function determines whether we should hide the cookie banner.
     * We treat ANY visible user choice (accept/decline/manage preferences) 
     * as a final interaction so the banner does not appear again.
     ********************************************************************/
  
    function hasUserInteractedWithConsent() {
        // we check our own cookie first
        const cookieValue = getCookie(CONSENT_COOKIE_NAME);
    
        if (cookieValue === 'accepted' ||
            cookieValue === 'declined' ||
            cookieValue === 'managed') {
               // log('User interaction found via our cookie:', cookieValue);
          return true;
        }
    
        // Use Shopify consent as fallback
        if (window.Shopify && window.Shopify.customerPrivacy) {
          try {
            const consent = Shopify.customerPrivacy.currentVisitorConsent();
            // log('Shopify consent object:', consent);
    
            if (consent) {
              const { marketing, analytics, preferences } = consent;
    
              const hasAnyYes =
                marketing === 'yes' ||
                analytics === 'yes' ||
                preferences === 'yes';

              const allNo =
                marketing === 'no' &&
                analytics === 'no' &&
                preferences === 'no';
    
              if (hasAnyYes) {
                // log('User interaction inferred from Shopify consent (one or more "yes"). Treating as accepted.');
                // we persist this decision in our own cookie for future checks
                saveConsent('accepted');
                return true;
              }

              if (allNo) {
                // log('User declined all cookies in Shopify consent. Treating as declined.');
                saveConsent('declined');
                return true;
              }
            }
          } catch (e) {
            console.warn('[ConsentFix] Error checking Shopify consent:', e);
          }
        }
    
        // If there is no cookie and no "yes in Shopify's consent, we treat it as "no decision yet"
        // log('No user consent interaction detected.');
        return false;
      }
  
  
    /********************************************************************
     * BANNER CONTROL
     ********************************************************************/
    
    function hideBannerIfNeeded() {
      if (hasUserInteractedWithConsent()) {
        const banner = document.getElementById(BANNER_ID);
        if (banner) {
          banner.style.display = 'none';
          // log('Banner hidden.');
        } else {
          // log('Banner not found — maybe already removed.');
        }
      } else {
        // log('Banner remains visible — no decision stored yet.');
      }
    }
  
  
    /********************************************************************
     * SAVE CONSENT
     ********************************************************************/
    
    function saveConsent(choice) {
      // log('Saving user choice:', choice);
      setCookie(CONSENT_COOKIE_NAME, choice, COOKIE_EXPIRY_DAYS);
    }
  
  
    /********************************************************************
     * CLICK EVENT SETUP
     *
     * Attaches listeners to ACCEPT and DECLINE buttons.
     * When clicked:
     *   1) We store the user's choice
     *   2) We immediately hide the banner (to prevent a flash)
     ********************************************************************/
    
    function attachBannerListeners() {
      const acceptBtn  = document.getElementById('shopify-pc__banner__btn-accept');
      const declineBtn = document.getElementById('shopify-pc__banner__btn-decline');
  
      if (acceptBtn) {
        acceptBtn.addEventListener('click', function() {
          // log('Accept clicked.');
          saveConsent('accepted');
          hideBannerIfNeeded();
        });
      }
  
      if (declineBtn) {
        declineBtn.addEventListener('click', function() {
          // log('Decline clicked.');
          saveConsent('declined');
          hideBannerIfNeeded();
        });
      }
    }
  
  
    /********************************************************************
     * MANAGE SETTINGS / BANNER OBSERVER
     *
     * Some users click "Manage settings" instead of directly Accept/Decline.
     * Shopify handles that flow internally and hides the banner afterwards.
     * We detect that automatic hide and interpret it as "managed" so we
     * don't show the banner again on future visits.
     ********************************************************************/
  
    function observeBannerForManageAction() {
        // If we already know the user has interacted, no need to observe.
        if (hasUserInteractedWithConsent()) {
          // log('User already has consent — skipping banner observer.');
          return;
        }
      
        const banner = document.getElementById(BANNER_ID);
        if (!banner) {
          // log('No banner found to observe for manage action.');
          return;
        }
      
        const observer = new MutationObserver(() => {
          const currentBanner = document.getElementById(BANNER_ID);
      
          // CASE 1: Banner removed from DOM
          if (!currentBanner) {
            // log('Banner removed from DOM — treating as "managed".');
      
            // we only set "managed" if we still don’t have our own cookie
            // to avoid overwriting an explicit accept/decline decision
            if (!getCookie(CONSENT_COOKIE_NAME)) {
              saveConsent('managed');
            }
      
            observer.disconnect();
            return;
          }
      
          const isHiddenByStyle = currentBanner.style.display === 'none';
          const isHiddenByClass = currentBanner.classList.contains('hidden');
      
          // if the banner is hidden via CSS
          if (isHiddenByStyle || isHiddenByClass) {
            // log('Banner hidden by Shopify — treating as "managed".');
      
            if (!getCookie(CONSENT_COOKIE_NAME)) {
              saveConsent('managed');
            }
      
            observer.disconnect();
          }
        });
      
        // Observe parent for child removal (banner removed from DOM)
        if (banner.parentNode) {
          observer.observe(banner.parentNode, {
            childList: true,
            subtree: false
          });
        }

        // Observe banner itself for attribute changes (style/class)
        observer.observe(banner, {
          attributes: true,
          attributeFilter: ['style', 'class']
        });
      
        // log('Started observing banner for manage-settings flow.');
      }
  
  
    /********************************************************************
     * INIT FUNCTION
     *
     * - First hides the banner if user has already interacted
     * - Then attaches click handlers
     * - Then observes the banner for "Manage settings" flows
     ********************************************************************/
    
    function init() {
      // log('Initializing consent fix script...');
      hideBannerIfNeeded();
      attachBannerListeners();
      observeBannerForManageAction();
    }


    /********************************************************************
     * WAIT FOR BANNER
     *
     * Shopify injects the cookie banner dynamically via its own JS.
     * This script may run before the banner exists in the DOM.
     * We use a MutationObserver on document.body to detect when
     * Shopify inserts the banner, then run init logic.
     ********************************************************************/

    function waitForBanner() {
      // Banner might already be in the DOM
      var existing = document.getElementById(BANNER_ID);
      if (existing) {
        // log('Banner already in DOM.');
        init();
        return;
      }

      // log('Banner not yet in DOM — observing for insertion...');
      var observer = new MutationObserver(function() {
        var banner = document.getElementById(BANNER_ID);
        if (banner) {
          observer.disconnect();
          init();
        }
      });

      observer.observe(document.body || document.documentElement, {
        childList: true,
        subtree: false
      });

      // Safety: stop waiting after 10 seconds
      setTimeout(function() {
        observer.disconnect();
      }, 10000);
    }
  
  
    /********************************************************************
     * EXECUTION LOGIC
     *
     * Shopify injects the banner asynchronously, so we must wait for
     * it rather than assuming it exists at DOMContentLoaded.
     ********************************************************************/
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', waitForBanner);
    } else {
      waitForBanner();
    }
  
  })();
  